// Hook for real-time duplicate detection - 95 lines ✅
// @requirement SPEC-IRO-001/FR-07: Real-time duplicate detection during recording

import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import type { SearchResult } from '@/types/knowledge'
import type { IssueRecorderOutput } from '@/types/ai'

interface UseDuplicateDetectionParams {
  extractedData: IssueRecorderOutput['extractedData']
  enabled?: boolean
  duplicateThreshold?: number
  similarThreshold?: number
  debounceMs?: number
}

interface UseDuplicateDetectionReturn {
  duplicates: SearchResult[]
  similarResolved: SearchResult[]
  isDetecting: boolean
  error: Error | null
}

export function useDuplicateDetection({
  extractedData,
  enabled = true,
  duplicateThreshold = 0.85,
  similarThreshold = 0.70,
  debounceMs = 1000,
}: UseDuplicateDetectionParams): UseDuplicateDetectionReturn {
  const [duplicates, setDuplicates] = useState<SearchResult[]>([])
  const [similarResolved, setSimilarResolved] = useState<SearchResult[]>([])
  const [isDetecting, setIsDetecting] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!enabled) return

    const searchText = extractedData.problemTitle || extractedData.problemDescription
    if (!searchText || searchText.length < 10) {
      setDuplicates([])
      setSimilarResolved([])
      return
    }

    const checkDuplicates = async (): Promise<void> => {
      setIsDetecting(true)
      setError(null)

      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return

        const { data: userRow } = await supabase
          .from('users')
          .select('organization_id')
          .eq('id', user.id)
          .single()

        if (!userRow?.organization_id) return

        // Semantic duplicate detection is inert until the embedding pipeline
        // exists. Two things must land before the RPCs below can be restored:
        //
        //   1. A server-side embedding step. This hook previously passed
        //      `searchText` (raw prose) as `query_embedding`. Postgres cannot
        //      parse prose as a vector, so every debounce tick produced
        //      `POST /rest/v1/rpc/search_similar_issues -> 400`.
        //   2. Real stored embeddings. issues.description_embedding and
        //      knowledge_entries.description_embedding are written as
        //      `new Array(1536).fill(0)` by every UI save path, so cosine
        //      distance against them is undefined even with a valid query
        //      vector. Existing rows need backfilling.
        //
        // Returning empty results keeps the caller's contract intact and stops
        // the request storm, without pretending a match was searched for.
        setDuplicates([])
        setSimilarResolved([])
      } catch (err) {
        console.error('Duplicate detection failed:', err)
        setError(err as Error)
      } finally {
        setIsDetecting(false)
      }
    }

    const debounced = setTimeout(checkDuplicates, debounceMs)
    return () => clearTimeout(debounced)
  }, [
    enabled,
    extractedData.problemTitle,
    extractedData.problemDescription,
    extractedData.d365Version,
    duplicateThreshold,
    similarThreshold,
    debounceMs,
  ])

  return {
    duplicates,
    similarResolved,
    isDetecting,
    error,
  }
}
